import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Search,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Download,
  Shield,
  CheckCircle2,
  Clock,
  User,
  Globe,
  FileJson,
  FileSpreadsheet,
  Activity,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AuditLogEntry {
  id: number;
  timestamp: string;
  user_name: string;
  user_email: string;
  action: string;
  resource_type: string;
  resource_id: string | number | null;
  ip_address: string | null;
  before_value: Record<string, any> | null;
  after_value: Record<string, any> | null;
  hash_valid?: boolean;
}

interface LogsResponse {
  logs: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
  chain_valid: boolean;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const ACTION_TYPES = ['', 'create', 'update', 'delete', 'login', 'logout', 'export', 'assign', 'approve', 'reject', 'acknowledge'];
const RESOURCE_TYPES = ['', 'user', 'policy', 'framework', 'evidence', 'task', 'risk', 'capa', 'control', 'audit', 'certificate', 'vendor', 'asset'];
const PAGE_SIZES = [25, 50, 100];

const ACTION_BADGE: Record<string, string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-slate-100 text-slate-700',
  logout: 'bg-slate-100 text-slate-500',
  export: 'bg-purple-100 text-purple-800',
  assign: 'bg-indigo-100 text-indigo-800',
  approve: 'bg-emerald-100 text-emerald-800',
  reject: 'bg-orange-100 text-orange-800',
  acknowledge: 'bg-teal-100 text-teal-800',
};

const fmtLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const fmtTimestamp = (d: string) => {
  const date = new Date(d);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' ' + date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

/* ------------------------------------------------------------------ */
/*  API helpers                                                        */
/* ------------------------------------------------------------------ */

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const API = '/api/compliance/audit-logs';

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(url, { headers: authHeaders(), ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  JSON Diff Component                                                */
/* ------------------------------------------------------------------ */

const JsonDiff: React.FC<{ before: Record<string, any> | null; after: Record<string, any> | null }> = ({ before, after }) => {
  if (!before && !after) return <p className="text-sm text-slate-400">No change data available.</p>;

  const allKeys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const changes: { key: string; old: any; new_: any; type: 'added' | 'removed' | 'changed' | 'unchanged' }[] = [];

  allKeys.forEach(key => {
    const oldVal = before?.[key];
    const newVal = after?.[key];
    if (oldVal === undefined && newVal !== undefined) {
      changes.push({ key, old: undefined, new_: newVal, type: 'added' });
    } else if (oldVal !== undefined && newVal === undefined) {
      changes.push({ key, old: oldVal, new_: undefined, type: 'removed' });
    } else if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes.push({ key, old: oldVal, new_: newVal, type: 'changed' });
    }
  });

  if (changes.length === 0) {
    return <p className="text-sm text-slate-400">No differences detected.</p>;
  }

  return (
    <div className="space-y-1.5">
      {changes.map(c => (
        <div key={c.key} className="flex items-start gap-2 text-xs font-mono">
          <span className="font-semibold text-slate-600 min-w-[120px] shrink-0">{c.key}:</span>
          <div className="flex-1 min-w-0">
            {c.type === 'added' && (
              <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded break-all">+ {JSON.stringify(c.new_)}</span>
            )}
            {c.type === 'removed' && (
              <span className="bg-red-50 text-red-700 px-1.5 py-0.5 rounded break-all">- {JSON.stringify(c.old)}</span>
            )}
            {c.type === 'changed' && (
              <div className="space-y-0.5">
                <span className="bg-red-50 text-red-700 px-1.5 py-0.5 rounded break-all inline-block">- {JSON.stringify(c.old)}</span>
                <br />
                <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded break-all inline-block">+ {JSON.stringify(c.new_)}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const TenantAuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [chainValid, setChainValid] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Expanded row
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Export
  const [exporting, setExporting] = useState<'csv' | 'json' | null>(null);

  /* ---------- Load ---------- */

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      if (actionFilter) params.set('action', actionFilter);
      if (resourceFilter) params.set('resource_type', resourceFilter);
      if (userSearch) params.set('user', userSearch);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const res = await apiFetch<LogsResponse>(`${API}?${params}`);
      setLogs(res.logs ?? []);
      setTotal(res.total ?? 0);
      setChainValid(res.chain_valid ?? true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, actionFilter, resourceFilter, userSearch, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [actionFilter, resourceFilter, userSearch, dateFrom, dateTo, pageSize]);

  /* ---------- Export ---------- */

  const handleExport = async (format: 'csv' | 'json') => {
    setExporting(format);
    try {
      const params = new URLSearchParams();
      params.set('format', format);
      if (actionFilter) params.set('action', actionFilter);
      if (resourceFilter) params.set('resource_type', resourceFilter);
      if (userSearch) params.set('user', userSearch);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const res = await fetch(`${API}/export?${params}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Export failed');

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setExporting(null);
    }
  };

  /* ---------- Pagination ---------- */

  const totalPages = Math.ceil(total / pageSize) || 1;

  /* ---------- Render ---------- */

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 rounded-xl">
            <Activity size={24} className="text-slate-600" />
          </div>
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Audit Logs</h1><PageInfoButton /></div>
            <p className="text-sm text-slate-500">Tenant-scoped activity and change tracking</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Chain validity indicator */}
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${chainValid ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {chainValid ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
            {chainValid ? 'Chain Valid' : 'Chain Broken'}
          </div>
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition">
            <RefreshCw size={16} /> Refresh
          </button>
          <div className="flex gap-1">
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting !== null}
              className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
            >
              <FileSpreadsheet size={14} /> {exporting === 'csv' ? 'Exporting...' : 'CSV'}
            </button>
            <button
              onClick={() => handleExport('json')}
              disabled={exporting !== null}
              className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
            >
              <FileJson size={14} /> {exporting === 'json' ? 'Exporting...' : 'JSON'}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
        <Search size={16} className="text-slate-400" />

        <select value={actionFilter} onChange={e => setActionFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All Actions</option>
          {ACTION_TYPES.filter(Boolean).map(a => <option key={a} value={a}>{fmtLabel(a)}</option>)}
        </select>

        <select value={resourceFilter} onChange={e => setResourceFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All Resources</option>
          {RESOURCE_TYPES.filter(Boolean).map(r => <option key={r} value={r}>{fmtLabel(r)}</option>)}
        </select>

        <input
          type="text"
          value={userSearch}
          onChange={e => setUserSearch(e.target.value)}
          placeholder="Filter by user..."
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm min-w-[140px] focus:ring-2 focus:ring-slate-500 focus:border-slate-500 outline-none"
        />

        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <span>From:</span>
          <input
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
            className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 outline-none"
          />
          <span>To:</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
            className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500 outline-none"
          />
        </div>

        {(actionFilter || resourceFilter || userSearch || dateFrom || dateTo) && (
          <button
            onClick={() => { setActionFilter(''); setResourceFilter(''); setUserSearch(''); setDateFrom(''); setDateTo(''); }}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600" />
        </div>
      ) : logs.length === 0 ? (
        <div className="py-16 bg-white border border-slate-200 rounded-xl">
          <Activity size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 text-lg font-medium">No audit log entries found</p>
          <p className="text-slate-400 text-sm mt-1">Adjust your filters or check back later.</p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['Timestamp', 'User', 'Action', 'Resource Type', 'Resource ID', 'IP Address', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map(entry => {
                    const isExpanded = expandedId === entry.id;
                    const badgeClass = ACTION_BADGE[entry.action] || 'bg-slate-100 text-slate-700';
                    return (
                      <React.Fragment key={entry.id}>
                        <tr
                          className="hover:bg-slate-50 cursor-pointer transition"
                          onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                        >
                          <td className="px-4 py-3 text-left text-xs text-slate-600 whitespace-nowrap">
                            <span className="flex items-center gap-1.5">
                              <Clock size={12} className="text-slate-400" />
                              {fmtTimestamp(entry.timestamp)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-left">
                            <span className="flex items-center gap-1.5 text-slate-700">
                              <User size={12} className="text-slate-400" />
                              {entry.user_name}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-left">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                              {fmtLabel(entry.action)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-left">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              {fmtLabel(entry.resource_type)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-left text-slate-600 font-mono text-xs">{entry.resource_id ?? '--'}</td>
                          <td className="px-4 py-3 text-left text-slate-500 text-xs">
                            {entry.ip_address ? (
                              <span className="flex items-center gap-1">
                                <Globe size={11} className="text-slate-400" />
                                {entry.ip_address}
                              </span>
                            ) : '--'}
                          </td>
                          <td className="px-4 py-3 text-left">
                            <div className="flex items-center gap-2">
                              {entry.hash_valid !== undefined && (
                                entry.hash_valid ? (
                                  <Shield size={14} className="text-green-500" title="Hash verified" />
                                ) : (
                                  <AlertTriangle size={14} className="text-red-500" title="Hash mismatch" />
                                )
                              )}
                              <button className="text-slate-400 hover:text-slate-600">
                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded diff */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="bg-slate-50 px-6 py-4 border-t border-slate-100">
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Change Details</p>
                                  {entry.hash_valid !== undefined && (
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${entry.hash_valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                      {entry.hash_valid ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />}
                                      {entry.hash_valid ? 'Hash Verified' : 'Hash Mismatch'}
                                    </span>
                                  )}
                                </div>
                                <div className="bg-white border border-slate-200 rounded-lg p-4">
                                  <JsonDiff before={entry.before_value} after={entry.after_value} />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>
                Showing {((page - 1) * pageSize) + 1}--{Math.min(page * pageSize, total || 0)} of {(total || 0).toLocaleString()}
              </span>
              <select
                value={pageSize}
                onChange={e => setPageSize(parseInt(e.target.value))}
                className="border border-slate-300 rounded-lg px-2 py-1 text-sm bg-white"
              >
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s} per page</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 text-sm rounded-lg transition ${
                      page === pageNum
                        ? 'bg-slate-900 text-white font-medium'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TenantAuditLogs;
